import { DeleteOutline } from '@mui/icons-material';
import { Grid, IconButton, Divider } from '@mui/material';
import PropTypes from 'prop-types';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useSelector } from 'react-redux';
import AddMoreButton from '../../../FormFields/AddMoreButton';
import FormTextField from '../../../FormFields/FormTextField';
import FormDatePickerField from '../../../FormFields/FormDatePickerField';
import { HR } from '../../../formQuestionsEnum';
import FormTitle from '../../../FormFields/FormTitle';

StaffOnLeaveSection.propTypes = {
  focusedField: PropTypes.string,
  setFocusedField: PropTypes.func,
  isEditable: PropTypes.bool,
};

export default function StaffOnLeaveSection({ focusedField, setFocusedField, isEditable }) {
  const { control, setValue } = useFormContext();
  const { fieldName } = HR.STAFF_ON_LEAVE;
  const { fields, append, remove } = useFieldArray({
    control,
    name: fieldName,
    keyName: '_id',
  });
  const watchedValues = useWatch({ control, name: fieldName }) || [];
  const isCanceled = useSelector((state) => state.transitions?.data?.isCanceled);
  const disabled = !isEditable || isCanceled;

  const visibleFields = fields
    .map((field, idx) => ({ field, idx }))
    .filter(({ idx }) => !watchedValues[idx]?.deleteFlag);

  const handleDelete = (idx) => {
    const rowId = watchedValues[idx]?.id;

    if (!rowId) {
      remove(idx);
      return;
    }

    setValue(`${fieldName}[${idx}].deleteFlag`, true, {
      shouldDirty: true,
    });
  };

  return (
    <>
      {visibleFields.map(({ field, idx }) => (
        <Grid xs={12} item container alignItems="center" spacing={1} key={field._id} sx={{ mb: 1 }}>
          <Grid container spacing={1} item xs={visibleFields.length > 1 ? 11.5 : 12}>
            <Grid item xs={12} md={6}>
              <FormTitle fieldAttributes={{ questionTitle: 'First name' }} />
              <FormTextField
                fieldName={`${fieldName}[${idx}].firstName`}
                fieldAttributes={{ isEditable }}
                focusedField={focusedField}
                setFocusedField={setFocusedField}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormTitle fieldAttributes={{ questionTitle: 'Last name' }} />
              <FormTextField
                fieldName={`${fieldName}[${idx}].lastName`}
                fieldAttributes={{ isEditable }}
                focusedField={focusedField}
                setFocusedField={setFocusedField}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <FormTitle fieldAttributes={{ questionTitle: 'Department' }} />
              <FormTextField
                fieldName={`${fieldName}[${idx}].department`}
                fieldAttributes={{ isEditable }}
                focusedField={focusedField}
                setFocusedField={setFocusedField}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <FormTitle fieldAttributes={{ questionTitle: 'Position' }} />
              <FormTextField
                fieldName={`${fieldName}[${idx}].position`}
                fieldAttributes={{ isEditable }}
                focusedField={focusedField}
                setFocusedField={setFocusedField}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <FormTitle fieldAttributes={{ questionTitle: 'Estimated return date' }} />
              <FormDatePickerField
                fieldName={`${fieldName}[${idx}].estimatedReturnDate`}
                fieldAttributes={{ isEditable }}
                focusedField={focusedField}
                setFocusedField={setFocusedField}
              />
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ mb: 1, mt: 2, borderWidth: 1 }} />
            </Grid>
          </Grid>

          {visibleFields.length > 1 && (
            <Grid item xs={0.5}>
              <IconButton onClick={() => handleDelete(idx)} disabled={disabled} color="error">
                <DeleteOutline />
              </IconButton>
            </Grid>
          )}
        </Grid>
      ))}
      <Grid xs={12} item>
        <AddMoreButton
          buttonLabel="Add more"
          handleAddMoreClick={() =>
            append({
              id: 0,
              firstName: null,
              lastName: null,
              department: null,
              position: null,
              estimatedReturnDate: null,
              deleteFlag: false,
            })
          }
          disabled={disabled}
        />
      </Grid>
    </>
  );
}
